import React, { useState, useEffect } from "react";
import {
    Button,
    Space,
    Tag,
    Typography,
    Popconfirm,
    message,
    Card,
    Switch,
    Tooltip,
    Modal,
    Input,
    Select,
    Form,
    Row,
    Col,
    ColorPicker,
    Spin,
    Empty,
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    CopyOutlined,
    EyeOutlined,
    CheckOutlined,
    CloseOutlined,
    SaveOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import { MoodQuestion, MoodOption } from "./MoodMeterDashboard";
import Datatable from "../../../core/common/dataTable";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Props {
    selectedCompany: {
        id: string | null;
        type: string | null;
        name: string;
    };
    onQuestionsChange?: () => void;
}

// Centralized API Service Class
class MoodQuestionService {
    private signature: string;

    constructor(signature: string) {
        this.signature = signature;
    }

    private handleResponse(response: any, operation: string) {
        if (!response.data) {
            throw new Error(`No response data for ${operation}`);
        }

        const status = response.data?.message?.[0]?.status || false;
        const data = response.data?.message?.[0]?.data || null;
        const errorMsg = response.data?.message?.[0]?.msg || response.data?.msg || `Operation failed: ${operation}`;

        return { status, data, errorMsg };
    }

    async getAllQuestions(company_id: string | null, company_type: string | null) {
        try {
            const response = await api.post("userService/getAllQuestions", {
                signature: this.signature,
                company_id,
                company_type,
            });

            const result = this.handleResponse(response, "fetch questions");

            if (result.status) {
                return result.data || [];
            } else {
                throw new Error(result.errorMsg);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            throw error;
        }
    }

    async createQuestion(payload: any) {
        try {
            const response = await api.post("userService/createQuestion", payload);

            const result = this.handleResponse(response, "create question");

            if (result.status) {
                return result.data || { id: Date.now().toString() };
            } else {
                throw new Error(result.errorMsg);
            }
        } catch (error) {
            console.error("Error creating question:", error);
            throw error;
        }
    }

    async updateQuestion(payload: any) {
        try {
            const response = await api.post("userService/updateQuestion", payload);

            const result = this.handleResponse(response, "update question");

            if (result.status) {
                return result.data || payload;
            } else {
                throw new Error(result.errorMsg);
            }
        } catch (error) {
            console.error("Error updating question:", error);
            throw error;
        }
    }

    async deleteQuestion(id: string | number, company_id: string | null, company_type: string | null) {
        try {
            const response = await api.post("userService/deleteQuestion", {
                signature: this.signature,
                id: typeof id === 'string' ? parseInt(id) : id,
                company_id,
                company_type,
            });

            const result = this.handleResponse(response, "delete question");

            if (result.status) {
                return true;
            } else {
                throw new Error(result.errorMsg);
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            throw error;
        }
    }
}

// Helper function to extract color value from ColorPicker
const getColorValue = (color: any): string => {
    if (!color) return "#cccccc";
    if (typeof color === 'string') return color;
    if (color && typeof color === 'object') {
        if (color.toHexString) return color.toHexString();
        if (color.hex) return color.hex;
    }
    return "#cccccc";
};

// Shared Form Component
interface QuestionFormProps {
    form: any;
    onFinish: (values: any) => void;
    loading?: boolean;
    mode?: 'create' | 'edit';
    emojiSuggestions?: string[];
}

const QuestionFormComponent: React.FC<QuestionFormProps> = ({
    form,
    onFinish,
    loading = false,
    mode = 'create',
    emojiSuggestions = ["😊", "🙂", "😐", "😟", "😢", "🎉", "🔥", "✅", "🌟", "❤️", "👍", "👎"],
}) => {
    const options = Form.useWatch('options', form) || [];

    const handleFinish = (values: any) => {
        const processedValues = {
            ...values,
            options: values.options?.map((opt: any) => ({
                ...opt,
                color: getColorValue(opt.color),
                score: typeof opt.score === 'string' ? parseInt(opt.score) : opt.score
            })) || []
        };

        onFinish(processedValues);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{
                status: 1,
                question_type: 'non_repetitive',
            }}
        >
            <Form.Item
                label="Question Text"
                name="question_text"
                rules={[
                    { required: true, message: 'Please enter question text' },
                    { min: 5, message: 'Question must be at least 5 characters' },
                ]}
            >
                <TextArea
                    rows={2}
                    placeholder="e.g., How are you feeling today?"
                    maxLength={200}
                    showCount
                />
            </Form.Item>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Question Type"
                        name="question_type"
                        rules={[{ required: true, message: 'Please select question type' }]}
                    >
                        <Select>
                            <Option value="non_repetitive">Non-Repetitive</Option>
                            <Option value="daily">Daily (Repetitive)</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Category"
                        name="category"
                    >
                        <Select placeholder="Select category" allowClear>
                            <Option value="mood">Mood</Option>
                            <Option value="workload">Workload</Option>
                            {/* <Option value="team">Team Collaboration</Option>
                            <Option value="workplace">Workplace Environment</Option> */}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            {mode === 'edit' && (
                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: 'Please select status' }]}
                >
                    <Select>
                        <Option value={1}>Active</Option>
                        <Option value={0}>Inactive</Option>
                    </Select>
                </Form.Item>
            )}

            <Card size="small" title="Response Options">
                <Form.List
                    name="options"
                    rules={[
                        {
                            validator: async (_, options) => {
                                if (!options || options.length < 2) {
                                    return Promise.reject(new Error('At least 2 options are required'));
                                }
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => {
                                // Get current values for this specific option using form.getFieldValue
                                const currentEmoji = form.getFieldValue(['options', name, 'emoji']);
                                const currentText = form.getFieldValue(['options', name, 'text']);
                                const currentColor = form.getFieldValue(['options', name, 'color']);

                                return (
                                    <Card
                                        key={key}
                                        size="small"
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Row gutter={12} align="middle">
                                            <Col span={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'emoji']}
                                                    rules={[{ required: true, message: 'Select emoji' }]}
                                                >
                                                    <Select
                                                        placeholder="Emoji"
                                                        size="small"
                                                        showSearch
                                                        optionFilterProp="children"
                                                        onChange={() => {
                                                            // Force re-render by updating form state
                                                            form.setFields([{ name: ['options', name, 'emoji'], touched: true }]);
                                                        }}
                                                    >
                                                        {emojiSuggestions.map(emoji => (
                                                            <Option key={emoji} value={emoji}>
                                                                {emoji}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={7}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'text']}
                                                    rules={[{ required: true, message: 'Enter text' }]}
                                                >
                                                    <Input
                                                        placeholder="Option text"
                                                        size="small"
                                                        onChange={() => {
                                                            form.setFields([{ name: ['options', name, 'text'], touched: true }]);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'color']}
                                                    rules={[{ required: true, message: 'Select color' }]}
                                                    getValueFromEvent={(color) => {
                                                        return color;
                                                    }}
                                                >
                                                    <ColorPicker
                                                        size="small"
                                                        showText
                                                        format="hex"
                                                        onChange={() => {
                                                            form.setFields([{ name: ['options', name, 'color'], touched: true }]);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'score']}
                                                    rules={[
                                                        { required: true, message: 'Enter score' },
                                                        { type: 'number', min: 1, max: 5, message: '1-5 only' }
                                                    ]}
                                                    normalize={(value) => {
                                                        if (value === '' || value === undefined || value === null) return undefined;
                                                        const num = Number(value);
                                                        return isNaN(num) ? undefined : num;
                                                    }}
                                                >
                                                    <Input
                                                        type="number"
                                                        min={1}
                                                        max={5}
                                                        placeholder="Score (1-5)"
                                                        size="small"
                                                        step={1}
                                                        onKeyPress={(e) => {
                                                            if (!/[0-9]/.test(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onChange={() => {
                                                            form.setFields([{ name: ['options', name, 'score'], touched: true }]);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Tag color={getColorValue(currentColor)}>
                                                        {currentEmoji || '?'}
                                                        {currentText || 'Option'}
                                                    </Tag>
                                                </div>
                                            </Col>
                                            <Col span={2}>
                                                <Button
                                                    danger
                                                    type="text"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(name)}
                                                    size="small"
                                                    disabled={fields.length <= 2}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            })}
                            <Form.ErrorList errors={errors} />
                            <Button
                                type="dashed"
                                onClick={() => {
                                    const newId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
                                    add({
                                        id: newId,
                                        text: `Option ${fields.length + 1}`,
                                        emoji: "😐",
                                        color: "#cccccc",
                                        score: 3,
                                    });
                                }}
                                block
                                size="small"
                                style={{ marginTop: 16 }}
                            >
                                Add Option
                            </Button>
                        </>
                    )}
                </Form.List>
            </Card>
        </Form>
    );
};
const createDefaultOptions = (): MoodOption[] => [
    {
        id: Date.now().toString() + "1",
        text: "Excellent",
        emoji: "😊",
        color: "#52c41a",
        score: 5
    },
    {
        id: Date.now().toString() + "2",
        text: "Good",
        emoji: "🙂",
        color: "#1890ff",
        score: 4
    },
    {
        id: Date.now().toString() + "3",
        text: "Neutral",
        emoji: "😐",
        color: "#faad14",
        score: 3
    },
    {
        id: Date.now().toString() + "4",
        text: "Stressed",
        emoji: "😟",
        color: "#ff7a45",
        score: 2
    },
];

const QuestionsList: React.FC<Props> = ({ selectedCompany, onQuestionsChange }) => {
    const signature = localStorage.getItem("signature") || "";
    const [questions, setQuestions] = useState<MoodQuestion[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingQuestion, setEditingQuestion] = useState<MoodQuestion | null>(null);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [addForm] = Form.useForm();
    const [saving, setSaving] = useState<boolean>(false);

    // Initialize API service
    const questionService = new MoodQuestionService(signature);

    // Fetch Questions
    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const rawQuestions = await questionService.getAllQuestions(
                selectedCompany.id,
                selectedCompany.type
            );

            const normalizedQuestions = rawQuestions.map((q: any) => ({
                ...q,
                id: q.id?.toString() || q._id?.toString() || Date.now().toString(),
                text: q.question_text || q.text || '',
                type: q.question_type || q.type || 'non_repetitive',
                options: Array.isArray(q.options) ? q.options : [],
                category: q.category || '',
                status: q.status !== undefined ? q.status : 1,
                active: q.status === 1,
            }));

            setQuestions(normalizedQuestions);
        } catch (error: any) {
            message.error(error.message || "Failed to load questions");
        } finally {
            setLoading(false);
        }
    };

    // Create Question
    const handleCreateQuestion = async (values: any) => {
        setSaving(true);
        try {

            const optionsWithIds = values.options.map((opt: any) => ({
                id: opt.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
                text: opt.text,
                emoji: opt.emoji,
                color: getColorValue(opt.color),
                score: typeof opt.score === 'string' ? parseInt(opt.score) : opt.score
            }));

            const payload = {
                signature,
                question_text: values.question_text,
                question_type: values.question_type || 'non_repetitive',
                options: optionsWithIds,
                category: values.category || null,
                company_id: selectedCompany.id ? parseInt(selectedCompany.id) : null,
                company_type: selectedCompany.type,
            };


            await questionService.createQuestion(payload);

            message.success("Question created successfully");
            setIsAddModalVisible(false);
            addForm.resetFields();
            fetchQuestions();
            if (onQuestionsChange) {
                onQuestionsChange();
            }
        } catch (error: any) {
            console.error("Create question error:", error);
            message.error(error.message || "Failed to create question");
        } finally {
            setSaving(false);
        }
    };

    // Update Question
    const handleUpdateQuestion = async (values: any) => {
        setSaving(true);
        try {
            // Process colors in options
            const processedOptions = values.options.map((opt: any) => ({
                ...opt,
                color: getColorValue(opt.color),
                score: typeof opt.score === 'string' ? parseInt(opt.score) : opt.score
            }));

            await questionService.updateQuestion({
                signature,
                id: editingQuestion?.id,
                question_text: values.question_text,
                question_type: values.question_type,
                options: processedOptions,
                category: values.category,
                status: values.status,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
            });

            message.success("Question updated successfully");
            setIsModalVisible(false);
            setEditingQuestion(null);
            form.resetFields();
            fetchQuestions();
            if (onQuestionsChange) {
                onQuestionsChange();
            }
        } catch (error: any) {
            message.error(error.message || "Failed to update question");
        } finally {
            setSaving(false);
        }
    };

    // Delete Question
    const handleDeleteQuestion = async (id: string) => {
        try {
            await questionService.deleteQuestion(
                id,
                selectedCompany.id,
                selectedCompany.type
            );
            message.success("Question deleted successfully");
            fetchQuestions();
            if (onQuestionsChange) {
                onQuestionsChange();
            }
        } catch (error: any) {
            message.error(error.message || "Failed to delete question");
        }
    };

    // Toggle Question Status
    const handleToggleStatus = async (question: MoodQuestion) => {
        try {
            const newStatus = question.status === 1 ? 0 : 1;
            await questionService.updateQuestion({
                signature,
                id: question.id,
                question_text: question.text,
                question_type: question.type,
                options: question.options,
                category: question.category,
                status: newStatus,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
            });
            message.success(`Question ${newStatus === 1 ? 'activated' : 'deactivated'}`);
            fetchQuestions();
        } catch (error: any) {
            message.error(error.message || "Error updating question status");
        }
    };

    // Initialize and open add modal with default values
    const openAddModal = () => {
        addForm.resetFields();

        const defaultOptionsWithIds = createDefaultOptions();

        addForm.setFieldsValue({
            question_text: '',
            question_type: 'non_repetitive',
            category: undefined,
            options: defaultOptionsWithIds,
        });

        setIsAddModalVisible(true);
    };

    // Initialize
    useEffect(() => {
        if (selectedCompany.id && selectedCompany.type) {
            fetchQuestions();
        }
    }, [selectedCompany]);

    const columns = [
        {
            title: 'Question',
            dataIndex: 'text',
            key: 'text',
            align: 'left',
            render: (text: string, record: MoodQuestion) => (
                <div className="d-flex flex-column">
                    <strong>{text}</strong>
                    <div className="d-flex gap-2 mt-1">
                        <span className="badge bg-primary-light text-primary">
                            {record.type}
                        </span>
                        {record.category && (
                            <span className="badge bg-success-light text-success">
                                {record.category}
                            </span>
                        )}
                        <span className="badge bg-info-light text-info">
                            {record.options.length} options
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: 'Options Preview',
            key: 'options',
            align: 'center',
            render: (_: any, record: MoodQuestion) => (
                <div className="d-flex gap-1 justify-content-center">
                    {record.options.slice(0, 3).map((opt, idx) => (
                        <Tooltip key={idx} title={`${opt.text} (${opt.score}/5)`}>
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    background: opt.color,
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                {opt.emoji}
                            </div>
                        </Tooltip>
                    ))}
                    {record.options.length > 3 && (
                        <span className="badge bg-secondary">
                            +{record.options.length - 3}
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: 'Question Type',
            key: 'question_type',
            align: 'center',
            render: (_: any, record: MoodQuestion) => {
                const formattedType = record.question_type
                    ?.replace(/_/g, ' ')
                    .replace(/\b\w/g, char => char.toUpperCase());

                return (
                    <Tooltip title={formattedType}>
                        <div
                            style={{
                                maxWidth: '220px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer'
                            }}
                        >
                            {formattedType}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (status: number, record: MoodQuestion) => (
                <div className="d-flex justify-content-center">
                    <Switch
                        checked={status === 1}
                        onChange={() => handleToggleStatus(record)}
                        size="small"
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                    />
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_: any, record: MoodQuestion) => (
                <div className="d-flex gap-1 justify-content-center">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                            setEditingQuestion(record);
                            form.setFieldsValue({
                                question_text: record.text,
                                question_type: record.type,
                                options: record.options,
                                category: record.category,
                                status: record.status,
                            });
                            setIsModalVisible(true);
                        }}
                        title="Edit"
                    >
                        <EditOutlined />
                    </button>
                    <Popconfirm
                        title="Delete this question?"
                        description="This will also delete all responses to this question."
                        onConfirm={() => handleDeleteQuestion(record.id)}
                        okText="Yes"
                        cancelText="No"
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    >
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                        >
                            <DeleteOutlined />
                        </button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    if (loading && questions.length === 0) {
        return (
            <div className="text-center py-5">
                <Spin size="large" />
                <div className="mt-3">
                    <Text type="secondary">Loading questions...</Text>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-1">Manage Questions ({questions.length})</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                            Create and manage mood questions for your team
                        </p>
                    </div>
                    <div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAddModal}
                            size="small"
                        >
                            Add Question
                        </Button>
                    </div>
                </div>
            </div>

            {/* Questions Table */}
            <Card size="small">
                <div className="p-3">
                    {questions.length === 0 ? (
                        <div className="text-center py-5">
                            <EyeOutlined className="display-4 text-muted mb-3" />
                            <p className="mb-2">No questions yet</p>
                            <Button
                                type="link"
                                size="small"
                                onClick={openAddModal}
                            >
                                Create your first question
                            </Button>
                        </div>
                    ) : (
                        <Datatable
                            columns={columns}
                            dataSource={questions}
                            Selection={false}
                        />
                    )}
                </div>
            </Card>

            {/* Edit Question Modal */}
            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <EditOutlined />
                        <span>Edit Question</span>
                    </div>
                }
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingQuestion(null);
                    form.resetFields();
                }}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => form.submit()}
                        loading={saving}
                    >
                        Update Question
                    </Button>,
                ]}
            >
                <QuestionFormComponent
                    form={form}
                    onFinish={handleUpdateQuestion}
                    loading={saving}
                    mode="edit"
                />
            </Modal>

            {/* Add Question Modal */}
            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <PlusOutlined />
                        <span>Add New Question</span>
                    </div>
                }
                open={isAddModalVisible}
                onCancel={() => {
                    setIsAddModalVisible(false);
                    addForm.resetFields();
                }}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsAddModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => {
                            addForm.validateFields()
                                .then(values => {
                                    handleCreateQuestion(values);
                                })
                                .catch(errorInfo => {
                                    message.error("Please check all required fields: " +
                                        errorInfo.errorFields.map((f: any) => f.errors.join(', ')).join(', '));
                                });
                        }}
                        loading={saving}
                    >
                        Create Question
                    </Button>,
                ]}
            >
                <QuestionFormComponent
                    form={addForm}
                    onFinish={handleCreateQuestion}
                    loading={saving}
                    mode="create"
                />
            </Modal>
        </div>
    );
};

export default QuestionsList;